import { round } from 'lodash';

import type { CartProductPhoto, CartProductPhotoParams } from 'domains/cart_products';
import type { Dimension } from 'types/common_types';
import type { ImageFilter } from 'types/photo_types';
import type { CropEntity } from './crops.types';
import type { ProductTemplateAPIData } from 'types/catalog_types';
import type { Dictionary } from '@reduxjs/toolkit';

type CropFilter = ImageFilter['filterType'];
type CartPhotoFilter = CartProductPhotoParams['style_filter'];

const filterMap: Record<CropFilter, CartPhotoFilter> = {
  original: undefined,
  bw: 'black_and_white',
  sepia1: 'sepia_a',
  sepia2: 'sepia_b',
};

const reversedFilterMap: Record<NonNullable<CartPhotoFilter>, CropFilter> = {
  black_and_white: 'bw',
  sepia_a: 'sepia1',
  sepia_b: 'sepia2',
};

function buildCropId(canvasId: string, nodeUid: string) {
  return `${canvasId}:${nodeUid}`;
}

function parseCropId(cropId: string) {
  const [canvasId, nodeUid] = cropId.split(':');
  return { canvasId, nodeUid };
}

function cropFilterForCartProduct(filter: CropFilter) {
  return filterMap[filter];
}

function cartProductFilterToCropFilter(filter: CartPhotoFilter): CropFilter {
  if (!filter) return 'original';

  return reversedFilterMap[filter];
}

function roundAspectRatio({ height, width }: Dimension) {
  return round(width / height, 4);
}

type RestoredCrop = Pick<
  CropEntity,
  | 'cropX'
  | 'cropY'
  | 'cropH'
  | 'cropW'
  | 'fitCropH'
  | 'fitCropW'
  | 'nodeAspectRatio'
  | 'zoom'
  | 'rotation'
>;

function restoreCartProductPhotoCrop(
  crop: CartProductPhoto['crop'],
  image: Dimension
): RestoredCrop {
  const cropW = parseFloat(crop.w) / 100;
  const cropH = parseFloat(crop.h) / 100;
  const cropX = parseFloat(crop.x) / 100;
  const cropY = parseFloat(crop.y) / 100;

  let fitCropH: number;
  let fitCropW: number;

  // We need to re-establish the fit crop values and zoom
  // Since we always crop to fit, the higher value will always be
  // the 'fit' dimension
  const fitDimension = Math.max(cropW, cropH);
  const cropAspectRatio = roundAspectRatio({ width: cropW, height: cropH });
  const nodeAspectRatio = roundAspectRatio({
    width: cropW * image.width,
    height: cropH * image.height,
  });

  if (fitDimension === cropW) {
    fitCropW = 1;
    fitCropH = cropAspectRatio;
  } else {
    fitCropH = 1;
    fitCropW = cropAspectRatio;
  }

  return {
    cropX,
    cropY,
    cropW,
    cropH,
    fitCropW,
    fitCropH,
    nodeAspectRatio,
    rotation: crop.rotation,
    zoom: 1 / fitDimension,
  };
}

function findNextAvailableCropId(
  cropsById: Dictionary<CropEntity>,
  product: ProductTemplateAPIData,
  canvasId: string
) {
  return product.nodes
    .filter((n) => n.node_type === 1)
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((node) => buildCropId(canvasId, node.uid))
    .find((cropId) => !cropsById[cropId]);
}

export default {
  buildCropId,
  parseCropId,
  cropFilterForCartProduct,
  cartProductFilterToCropFilter,
  roundAspectRatio,
  restoreCartProductPhotoCrop,
  findNextAvailableCropId,
};
